import { useEffect } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { monitoringPlanAPI } from "@/lib/monitoring-plan-api";
import { queryKeys } from "@/lib/query-keys";
import type {
  StopMonitoringPlanEntryRequest,
  UpsertMonitoringPlanEntryRequest,
} from "@/types";

export const useMonitoringPlan = (
  patientId: string,
  appointmentId?: string
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.consultation.patientData.monitoringPlan(
      patientId,
      appointmentId
    ),
    queryFn: () => monitoringPlanAPI.getPlan(patientId, appointmentId),
    enabled: !!patientId,
  });

  // onSettled: a timed-out or failed save may still have reached the server.
  // Refresh the plan, its change log and the picker's tracker list.
  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.consultation.patientData.monitoringPlanAll(patientId),
    });
    queryClient.invalidateQueries({
      queryKey:
        queryKeys.consultation.patientData.monitoringPlanChanges(patientId),
    });
    queryClient.invalidateQueries({
      queryKey: [...queryKeys.consultation.catalogs.all(), "trackers"],
    });
  };

  const upsertEntry = useMutation({
    mutationFn: (data: UpsertMonitoringPlanEntryRequest) =>
      monitoringPlanAPI.upsertEntry(patientId, data),
    onSettled: refresh,
  });

  const stopEntry = useMutation({
    mutationFn: ({
      entryId,
      data,
    }: {
      entryId: string;
      data: StopMonitoringPlanEntryRequest;
    }) => monitoringPlanAPI.stopEntry(patientId, entryId, data),
    onSettled: refresh,
  });

  return { ...query, upsertEntry, stopEntry };
};

export const useMonitoringPlanChanges = (
  patientId: string,
  enabled: boolean = true
) =>
  useInfiniteQuery({
    queryKey:
      queryKeys.consultation.patientData.monitoringPlanChanges(patientId),
    queryFn: ({ pageParam }) =>
      monitoringPlanAPI.getChanges(patientId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      last.next ? pages.length + 1 : undefined,
    enabled: enabled && !!patientId,
  });

// Every change record for the patient (all pages, newest first). The log is
// small, so pages are pulled until exhausted; `isComplete` is false until then.
export const useAllMonitoringPlanChanges = (
  patientId: string,
  enabled: boolean = true
) => {
  const query = useMonitoringPlanChanges(patientId, enabled);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const changes = query.data?.pages.flatMap((p) => p.results);
  return {
    changes: changes ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    isComplete: !!query.data && !hasNextPage,
  };
};

// The plan as it stood at `asOf`. Lives under the plan key prefix, so any
// plan mutation refreshes it too.
export const useMonitoringPlanAsOf = (
  patientId: string,
  asOf: string | null,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: queryKeys.consultation.patientData.monitoringPlan(
      patientId,
      `as_of:${asOf}`
    ),
    queryFn: () => monitoringPlanAPI.getPlanAsOf(patientId, asOf as string),
    enabled: enabled && !!patientId && !!asOf,
  });
